import { useQuery } from '@apollo/client';
import { Save, VerifiedUser } from '@mui/icons-material';
import { Alert, Box, Button } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { boolean, object, string } from 'yup';
import { applyServerErrors } from '~/api/helpers';
import { useUpdateRole } from '~/api/roles';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { DetailSwitch, DetailTextArea, DetailTextField } from '~/components/forms/details';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import {
  SettingDescription,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
  SettingsIntro,
} from '~/components/settings';
import { Timestamps } from '~/components/timestamps';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { i18n } from '~/lib/i18n';
import { useTabs } from '../lib/tabs';
import { RoleSettingsDocument } from './RoleSettings.generated';

const validationSchema = object({
  accessDevices: boolean().required(),
  accessFonts: boolean().required(),
  accessLiveChannelStreams: boolean().required(),
  accessReporting: boolean().required(),
  accessScheduler: boolean().required(),
  accessStudio: boolean().required(),
  accessTemplates: boolean().required(),
  activateDevices: boolean().required(),
  createDataSources: boolean().required(),
  createEvents: boolean().required(),
  createPlaylists: boolean().required(),
  createScheduledShows: boolean().required(),
  createShows: boolean().required(),
  createTemplates: boolean().required(),
  description: string().trim().label(i18n.t('common.labels.description')),
  name: string().required().trim().label(i18n.t('common.labels.name')),
});

export const RoleSettings = () => {
  const { t } = useTranslation();
  const params = useParams<{ roleId: string }>();
  const roleId = parseInt(params.roleId ?? '');

  const { currentNetwork, currentUser } = useAppContext();
  const tabs = useTabs();

  const [updateRole] = useUpdateRole();

  const { data, loading, error } = useQuery(RoleSettingsDocument, {
    variables: { networkId: currentNetwork.id, roleId },
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      accessDevices: data?.network?.role.accessDevices ?? false,
      accessFonts: data?.network?.role.accessFonts ?? false,
      accessLiveChannelStreams: data?.network?.role.accessLiveChannelStreams ?? false,
      accessReporting: data?.network?.role.accessReporting ?? false,
      accessScheduler: data?.network?.role.accessScheduler ?? false,
      accessStudio: data?.network?.role.accessStudio ?? false,
      accessTemplates: data?.network?.role.accessTemplates ?? false,
      activateDevices: data?.network?.role.activateDevices ?? false,
      createDataSources: data?.network?.role.createDataSources ?? false,
      createEvents: data?.network?.role.createEvents ?? false,
      createPlaylists: data?.network?.role.createPlaylists ?? false,
      createScheduledShows: data?.network?.role.createScheduledShows ?? false,
      createShows: data?.network?.role.createShows ?? false,
      createTemplates: data?.network?.role.createTemplates ?? false,
      description: data?.network?.role.description ?? '',
      name: data?.network?.role.name ?? '',
    },
    onSubmit: async (values, { setErrors }) => {
      const result = await updateRole({
        variables: { patch: validationSchema.cast(values), roleId },
      });
      applyServerErrors(result, setErrors);
    },
    validationSchema,
  });

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  const saveButton = (
    <Button
      color="primary"
      disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
      form="update-role"
      type="submit"
      variant="contained"
      startIcon={<Save />}
    >
      {t('common.buttons.save')}
    </Button>
  );

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      {data?.network && (
        <>
          <Toolbar
            actions={saveButton}
            breadcrumbsLabel={<RouterBreadcrumbs />}
            titleIcon={<VerifiedUser />}
            titleText={data.network.role.name}
          />

          <ScrollableRouterTabs current={t('common.labels.details')} tabs={tabs} />

          <PageContainer>
            <Box
              component="form"
              id="update-role"
              onSubmit={formik.handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <div>
                <SettingsHead>{t('common.labels.general')}</SettingsHead>
                <SettingsBody>
                  <SettingsData>
                    <SettingLabelWrapper>
                      <SettingName>{t('common.labels.name')}</SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.name.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailTextField
                        autoFocus
                        disabled={formik.isSubmitting}
                        error={formik.touched.name && !!formik.errors.name}
                        fullWidth
                        helperText={(formik.touched.name && formik.errors.name) || ' '}
                        name="name"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.name}
                      />
                    </SettingInputWrapper>
                  </SettingsData>

                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>{t('common.labels.description')}</SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.description.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailTextArea
                        error={formik.touched.description && !!formik.errors.description}
                        disabled={formik.isSubmitting}
                        fullWidth
                        helperText={
                          (formik.touched.description && formik.errors.description) || ' '
                        }
                        name="description"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.description}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </SettingsBody>
              </div>

              <div>
                <SettingsHead>
                  {t('pages.roles.settings.sections.entry_point_permissions')}
                </SettingsHead>
                <SettingsIntro>
                  <Alert severity="info">{t('pages.roles.settings.entry_point_info')}</Alert>
                </SettingsIntro>
                <SettingsBody>
                  <SettingsData>
                    <SettingLabelWrapper>
                      <SettingName>{t('common.labels.devices')}</SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.access_devices.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.accessDevices}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="accessDevices"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                  <SettingsData>
                    <SettingLabelWrapper>
                      <SettingName>{t('common.tabs.events')}</SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.access_scheduler.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.accessScheduler}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="accessScheduler"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                  <SettingsData>
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.access_fonts.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.access_fonts.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.accessFonts}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="accessFonts"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                  {data.network.settings.enableLiveChannelStreaming && (
                    <SettingsData>
                      <SettingLabelWrapper>
                        <SettingName>
                          {t('pages.roles.settings.fields.access_live_channel_streams.label')}
                        </SettingName>
                        <SettingDescription>
                          {t('pages.roles.settings.fields.access_live_channel_streams.description')}
                        </SettingDescription>
                      </SettingLabelWrapper>
                      <SettingInputWrapper>
                        <DetailSwitch
                          checked={formik.values.accessLiveChannelStreams}
                          color="primary"
                          disabled={formik.isSubmitting}
                          name="accessLiveChannelStreams"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                        />
                      </SettingInputWrapper>
                    </SettingsData>
                  )}
                  <SettingsData>
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.access_reporting.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.access_reporting.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.accessReporting}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="accessReporting"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                  <SettingsData>
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.access_studio.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.access_studio.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.accessStudio}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="accessStudio"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.access_templates.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.access_templates.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.accessTemplates}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="accessTemplates"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </SettingsBody>
              </div>

              <div>
                <SettingsHead>
                  {t('pages.roles.settings.sections.data_source_permissions')}
                </SettingsHead>
                <SettingsBody>
                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.create_data_sources.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.create_data_sources.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.createDataSources}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="createDataSources"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </SettingsBody>
              </div>

              <div>
                <SettingsHead>{t('pages.roles.settings.sections.device_permissions')}</SettingsHead>
                <SettingsBody>
                  <SettingsData>
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.activate_devices.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.activate_devices.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.activateDevices}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="activateDevices"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.create_scheduled_shows.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.create_scheduled_shows.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.createScheduledShows}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="createScheduledShows"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </SettingsBody>
              </div>

              <div>
                <SettingsHead>{t('pages.roles.settings.sections.event_permissions')}</SettingsHead>
                <SettingsBody>
                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.create_events.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.create_events.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.createEvents}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="createEvents"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </SettingsBody>
              </div>

              <div>
                <SettingsHead>
                  {t('pages.roles.settings.sections.playlist_permissions')}
                </SettingsHead>
                <SettingsBody>
                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.create_playlists.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.create_playlists.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.createPlaylists}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="createPlaylists"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </SettingsBody>
              </div>

              <div>
                <SettingsHead>{t('pages.roles.settings.sections.show_permissions')}</SettingsHead>
                <SettingsBody>
                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.create_shows.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.create_shows.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.createShows}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="createShows"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </SettingsBody>
              </div>

              <div>
                <SettingsHead>
                  {t('pages.roles.settings.sections.template_permissions')}
                </SettingsHead>
                <SettingsBody>
                  <SettingsData className="last">
                    <SettingLabelWrapper>
                      <SettingName>
                        {t('pages.roles.settings.fields.create_templates.label')}
                      </SettingName>
                      <SettingDescription>
                        {t('pages.roles.settings.fields.create_templates.description')}
                      </SettingDescription>
                    </SettingLabelWrapper>
                    <SettingInputWrapper>
                      <DetailSwitch
                        checked={formik.values.createTemplates}
                        color="primary"
                        disabled={formik.isSubmitting}
                        name="createTemplates"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                      />
                    </SettingInputWrapper>
                  </SettingsData>
                </SettingsBody>
              </div>

              {currentUser.admin && (
                <Timestamps
                  createdAt={data.network.role.createdAt ?? ''}
                  updatedAt={data.network.role.updatedAt ?? ''}
                />
              )}
            </Box>
          </PageContainer>
        </>
      )}
    </LoadingPane>
  );
};
